import { useState } from "react";
import { motion } from "framer-motion";
import { Maximize2 } from "lucide-react";
import { FadeUp } from "@/components/Reveal";
import { Lightbox } from "@/components/Lightbox";

const SHOTS = [
  { src: "/shots/map.png", title: "Find care faster", sub: "Live ER wait times on a map" },
  { src: "/shots/compare.png", title: "Compare wait times", sub: "Every nearby ER and clinic, ranked" },
  { src: "/shots/chat.png", title: "AI triage guidance", sub: "Not sure where to go? Ask" },
  { src: "/shots/alerts.png", title: "Live wait alerts", sub: "Get pinged when a wait drops" },
  { src: "/shots/doctors.png", title: "Verified doctor reviews", sub: "Real ratings from real visits" },
  { src: "/shots/pricing.png", title: "Become a founding member", sub: "First 500 members get 20% off yearly — for life" },
];

export const Gallery = () => {
  const [active, setActive] = useState(null);
  return (
  <section data-testid="screenshot-gallery" className="relative bg-transparent py-24 sm:py-32 lg:py-40 overflow-hidden">
    <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
      <FadeUp className="flex items-end justify-between gap-6">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.18em] text-[var(--clinical)] flex items-center gap-3">
            <span className="h-px w-8 bg-[var(--clinical)]" /> Screens
          </p>
          <h2 className="font-display mt-5 text-3xl sm:text-4xl font-bold tracking-tight text-white leading-[1.05]">Everything, one thumb away.</h2>
        </div>
        <p className="hidden sm:block text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-500">Tap to enlarge · Tap again to zoom</p>
      </FadeUp>
    </div>

    <div className="mt-10 sm:mt-14 overflow-x-auto snap-x snap-mandatory scrollbar-none [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
      <ul className="flex gap-5 sm:gap-8 px-4 sm:px-8 mx-auto w-max">
        {SHOTS.map((s, i) => (
          <motion.li
            key={s.src}
            data-testid={`gallery-shot-${i}`}
            initial={{ opacity: 0, y: 40, rotate: i % 2 ? 1.5 : -1.5 }}
            whileInView={{ opacity: 1, y: 0, rotate: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1], delay: i * 0.08 }}
            className="snap-center shrink-0 w-[240px] sm:w-[280px] group"
          >
            <button
              type="button"
              onClick={() => setActive(i)}
              data-testid={`gallery-shot-button-${i}`}
              aria-label={`Open ${s.title}`}
              className="relative block w-full overflow-hidden rounded-[1.75rem] bg-transparent shadow-[0_30px_80px_-30px_rgba(0,0,0,0.8)] transition-transform duration-500 group-hover:-translate-y-2 cursor-zoom-in focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--clinical)]"
            >
              <img src={s.src} alt={`${s.title} — ${s.sub}`} loading="lazy" width="720" height="1280" className="block w-full h-auto" />
              <div className="pointer-events-none absolute inset-0 ring-1 ring-inset ring-white/5" />
              <span className="pointer-events-none absolute bottom-4 right-4 inline-flex h-9 w-9 items-center justify-center rounded-full bg-[#0a1220]/70 text-white opacity-0 translate-y-1 transition-[opacity,transform] duration-300 group-hover:opacity-100 group-hover:translate-y-0">
                <Maximize2 size={15} />
              </span>
            </button>
          </motion.li>
        ))}
      </ul>
    </div>
    <Lightbox shots={SHOTS} index={active} onClose={() => setActive(null)} onChange={setActive} />
  </section>
  );
};
